// The console's root: sign-in by email link, first-visit account details, then the shell and the
// page for the current route.
import { createContext, lazy, Suspense, useContext, useEffect, useState, type FormEvent } from "react";
import type { AuthUser } from "@estra/client/auth";
import { doc, serverTimestamp, setDoc, type DocumentData } from "@estra/client/firestore";
import { auth, db, errText } from "./api.ts";
import { useDoc, useProjects, useUser, type Project } from "./data/hooks.ts";
import { legacyRedirect, useRoute } from "./router.ts";
import { Shell } from "./shell/Shell.tsx";
import { Home } from "./pages/Home.tsx";
import { NewProject } from "./pages/NewProject.tsx";
import { AccountPage } from "./pages/Account.tsx";
import { ProjectPage } from "./pages/project/ProjectPage.tsx";
const AdminPage = lazy(() => import("./pages/admin/AdminPage.tsx").then((m) => ({ default: m.AdminPage })));

export interface ConsoleCtx {
  user: AuthUser;
  account: DocumentData & { id: string };
  isAdmin: boolean;
  projects: Project[] | undefined;
}
const Ctx = createContext<ConsoleCtx | null>(null);
export const useConsole = () => useContext(Ctx)!;

export function App() {
  const user = useUser();
  if (user === undefined) return <div className="boot"><span className="boot-star" /></div>;
  if (!user) return <SignIn />;
  return <SignedIn user={user} />;
}

function SignedIn({ user }: { user: AuthUser }) {
  const account = useDoc(`accounts/${user.uid}`);
  const projects = useProjects(user.uid);
  const route = useRoute();
  useEffect(() => {
    const to = legacyRedirect(location.hash);
    if (to) location.replace(to);
  }, [route]);
  if (account.error) return <div className="main"><div className="page narrow"><p className="bad">{errText(account.error)}</p></div></div>;
  if (account.data === undefined) return <div className="boot"><span className="boot-star" /></div>;
  if (account.data === null) return <Welcome user={user} />;
  const ctx: ConsoleCtx = { user, account: account.data, isAdmin: account.data.role === "admin", projects: projects.data };
  let page;
  if (route.name === "project" && route.id) page = <ProjectPage key={route.id} id={route.id} route={route} />;
  else if (route.name === "new") page = <NewProject source={route.rest[0] as "fresh" | "firebase" | undefined} />;
  else if (route.name === "admin") page = <Suspense fallback={<div className="boot"><span className="boot-star" /></div>}><AdminPage route={route} /></Suspense>;
  else if (route.name === "account") page = <AccountPage />;
  else page = <Home />;
  return (
    <Ctx.Provider value={ctx}>
      <Shell route={route}>{page}</Shell>
    </Ctx.Provider>
  );
}

function Panel({ eyebrow, title, children }: { eyebrow: string; title: string; children?: React.ReactNode }) {
  return (
    <div className="signin">
      <div className="signin-card">
        <a className="mark" href="/" aria-label="Estra home">
          <svg viewBox="0 0 32 32" aria-hidden="true"><path d="M16 3l3 10 10 3-10 3-3 10-3-10-10-3 10-3z" /></svg>
          <span>estra</span>
        </a>
        <p className="eyebrow" style={{ marginTop: 40 }}>{eyebrow}</p>
        <h1>{title}</h1>
        {children}
      </div>
      <div className="signin-art" aria-hidden="true">
        <p>Your app's backend, <em>alive</em> in one place.</p>
      </div>
    </div>
  );
}

function SignIn() {
  const [email, setEmail] = useState("");
  const [sent, setSent] = useState(false);
  const [msg, setMsg] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      await auth.sendSignInLink(email.trim());
      setSent(true);
    } catch (err) {
      setMsg(errText(err));
      setBusy(false);
    }
  };
  if (sent) return <Panel eyebrow="Check your inbox" title="We sent you a link."><p className="muted" style={{ marginTop: 12 }}>Open it on this device to sign in as {email.trim()}. It works once and expires in an hour.</p></Panel>;
  return (
    <Panel eyebrow="Estra Cloud · free beta" title="Sign in to your console.">
      <p className="muted" style={{ marginTop: 12 }}>Start a new app from a starter, or move your Firebase app over. No password, no card.</p>
      <form className="form-grid" style={{ marginTop: 24 }} onSubmit={submit}>
        <label>Work email<input type="email" required placeholder="you@company.com" autoComplete="email" value={email} onChange={(e) => setEmail(e.target.value)} /></label>
        <button className="btn" type="submit" disabled={busy}>Email me a sign-in link</button>
        {msg && <p className="small bad">{msg}</p>}
      </form>
    </Panel>
  );
}

/** auth.html: the email link lands here; Cloudflare static assets serve /app/auth.html as /app/auth (query kept). */
export function FinishLink() {
  const [err, setErr] = useState<string | null>(null);
  useEffect(() => {
    const code = new URLSearchParams(location.search).get("oobCode");
    (async () => {
      if (!code) throw new Error("This link is missing its code.");
      await auth.signInWithLink(code);
      location.replace("/app/");
    })().catch((e) => setErr(errText(e)));
  }, []);
  if (!err) return <Panel eyebrow="Signing you in" title="One moment…" />;
  return (
    <Panel eyebrow="Sign-in link" title="That link didn't work.">
      <p className="muted" style={{ marginTop: 12 }}>{err.replace(/\.?$/, ".")} Links work once and expire after an hour.</p>
      <a className="btn" href="/app/" style={{ marginTop: 20 }}>Send a new link</a>
    </Panel>
  );
}

function Welcome({ user }: { user: AuthUser }) {
  const [name, setName] = useState(user.displayName ?? "");
  const [company, setCompany] = useState("");
  const [msg, setMsg] = useState("");
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await setDoc(doc(db, `accounts/${user.uid}`), { email: user.email, name: name.trim(), company: company.trim(), role: "customer", createdAt: serverTimestamp(), updatedAt: serverTimestamp() });
    } catch (err) {
      setMsg(errText(err));
    }
  };
  return (
    <Panel eyebrow="Welcome" title="Tell us who you are.">
      <form className="form-grid" style={{ marginTop: 24 }} onSubmit={submit}>
        <label>Your name<input required autoComplete="name" value={name} onChange={(e) => setName(e.target.value)} /></label>
        <label>Company (optional)<input autoComplete="organization" value={company} onChange={(e) => setCompany(e.target.value)} /></label>
        <button className="btn" type="submit">Continue</button>
        {msg && <p className="small bad">{msg}</p>}
      </form>
    </Panel>
  );
}
